/**
 * Compakt timer med heartbeat-baserad "suspected"-segment-detektering.
 *
 * State-modellen:
 *   * En session = matter + samling av tidssegment + running-flag.
 *   * Segment = start/end + suspected + included (checkbox-vald).
 *   * En segment i taget är "aktiv" (currentSegmentStart är då satt).
 *
 * Heartbeat-loopen tickar var 15:e sekund när isRunning. Om två
 * heartbeats är >45 sek isär tolkar vi det som förlorat spårnings-
 * fönster (browser sov, användaren låste datorn, add-in:et pausades
 * av OS m.m.). Vi:
 *   1. Stänger det aktiva segmentet vid senaste heartbeat-tiden.
 *   2. Skapar ett "suspected"-segment för gapet.
 *   3. Startar ett nytt aktivt segment nu.
 *
 * VIKTIGT: Vi tolkar ALDRIG idle-input (icke-skrivning, inga klick)
 * som förlorad tid. Juristen kan sitta och läsa/tänka. Suspected
 * markeras enbart när vi tekniskt förlorade spårningen.
 *
 * Persistens: hela state sparas i localStorage så att timern
 * överlever taskpane-reload / add-in-omstart.
 */

const STORAGE_KEY = "sigge_timer_v1";
const HEARTBEAT_MS = 15_000;
const SUSPECT_THRESHOLD_MS = 45_000;

export interface TimerSegment {
  id: string;
  startISO: string;
  endISO: string;
  suspected: boolean;
  included: boolean;
}

export interface TimerState {
  sessionId: string;
  caseId: number | null;
  caseTitle: string | null;
  segments: TimerSegment[];
  isRunning: boolean;
  currentSegmentStart: string | null;
  lastHeartbeat: string;
  note: string;
  billable: boolean;
  createdAt: string;
}

function uuid(): string {
  // Modern browsers har crypto.randomUUID — Word-taskpanen kör i
  // en modern edge-webview så det är säkert att anta det.
  return (globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random()));
}

function nowISO(): string {
  return new Date().toISOString();
}

export function loadTimer(): TimerState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TimerState;
  } catch {
    return null;
  }
}

export function saveTimer(state: TimerState | null): void {
  try {
    if (state === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch {
    /* localStorage kvot / private mode — timern försämras då till
       pure-memory. Ingen krasch. */
  }
}

export function newTimer(caseId: number, caseTitle: string): TimerState {
  const now = nowISO();
  return {
    sessionId: uuid(),
    caseId,
    caseTitle,
    segments: [],
    isRunning: true,
    currentSegmentStart: now,
    lastHeartbeat: now,
    note: "",
    billable: true,
    createdAt: now,
  };
}

/**
 * Tickar en heartbeat på det pågående state:t. Returnerar nytt state.
 * Om gapet sedan senaste heartbeat är stort skapar vi suspected-
 * segmentet och startar om det aktiva segmentet.
 */
export function tickHeartbeat(state: TimerState): TimerState {
  if (!state.isRunning || !state.currentSegmentStart) return state;
  const now = Date.now();
  const last = new Date(state.lastHeartbeat).getTime();
  const gap = now - last;

  if (gap <= SUSPECT_THRESHOLD_MS) {
    // Normal tick — bara updatera heartbeat, segmentet fortsätter.
    return { ...state, lastHeartbeat: nowISO() };
  }

  // Gap upptäckt: dela upp i tre delar.
  const nowIso = nowISO();
  const priorSegment: TimerSegment = {
    id: uuid(),
    startISO: state.currentSegmentStart,
    endISO: state.lastHeartbeat,
    suspected: false,
    included: true,
  };
  const suspectedSegment: TimerSegment = {
    id: uuid(),
    startISO: state.lastHeartbeat,
    endISO: nowIso,
    suspected: true,
    included: false,
  };
  return {
    ...state,
    segments: [...state.segments, priorSegment, suspectedSegment],
    currentSegmentStart: nowIso,
    lastHeartbeat: nowIso,
  };
}

/**
 * Stänger den aktiva delen och sätter isRunning=false. Anropas när
 * juristen klickar Stopp.
 */
export function stopTimer(state: TimerState): TimerState {
  if (!state.isRunning || !state.currentSegmentStart) {
    return { ...state, isRunning: false, currentSegmentStart: null };
  }
  const closing: TimerSegment = {
    id: uuid(),
    startISO: state.currentSegmentStart,
    endISO: nowISO(),
    suspected: false,
    included: true,
  };
  return {
    ...state,
    isRunning: false,
    currentSegmentStart: null,
    segments: [...state.segments, closing],
  };
}

export function toggleSegment(state: TimerState, segmentId: string): TimerState {
  return {
    ...state,
    segments: state.segments.map((s) =>
      s.id === segmentId ? { ...s, included: !s.included } : s
    ),
  };
}

export function removeSuspected(state: TimerState): TimerState {
  return {
    ...state,
    segments: state.segments.map((s) =>
      s.suspected ? { ...s, included: false } : s
    ),
  };
}

export function totalIncludedMinutes(state: TimerState): number {
  const ms = state.segments
    .filter((s) => s.included)
    .reduce(
      (acc, s) => acc + (new Date(s.endISO).getTime() - new Date(s.startISO).getTime()),
      0
    );
  return Math.round(ms / 60_000);
}

export function runningDurationMs(state: TimerState): number {
  const closed = state.segments.reduce(
    (acc, s) => acc + (new Date(s.endISO).getTime() - new Date(s.startISO).getTime()),
    0
  );
  const active =
    state.isRunning && state.currentSegmentStart
      ? Date.now() - new Date(state.currentSegmentStart).getTime()
      : 0;
  return closed + active;
}

export function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
}

export const TIMER_CONSTANTS = {
  HEARTBEAT_MS,
  SUSPECT_THRESHOLD_MS,
};
